import { useRef } from 'react';
import { CheckCircle2, Loader2, X, XCircle } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { useMe } from '@/lib/hooks';
import { dismissRun, reportParts, runPercent, type BackupReport, type BotRun } from '@/lib/runs';

/**
 * Shared parts of the Backups page and the Admin-Panel clone: the four backup
 * areas (bot plugins/Admin/lib/backupOps.js), the progress of a run (bar,
 * counter, time left — lib/runs.ts) and the bot's final report.
 */

export type BackupArea = 'structure' | 'settings' | 'data' | 'access';

export const AREA_INFO: Array<{ key: BackupArea; label: string; hint: string }> = [
  { key: 'structure', label: 'Rollen & Channels', hint: 'Rollen, Kategorien, Kanäle mit Berechtigungen, Custom Emojis, Server-Icon' },
  { key: 'settings', label: 'Bot-Einstellungen & Panels', hint: 'Alle Module, Panels, Self-Roles, Games-Katalog, Schalter, Startrolle, Command-Perms' },
  { key: 'data', label: 'Bot-Daten', hint: 'XP & Level, Warnungen, Permabans' },
  { key: 'access', label: 'Dashboard-Zugriffsrechte', hint: 'Vorlagen und Personen dieses Servers' },
];
export const DEFAULT_AREAS: BackupArea[] = ['structure', 'settings'];
export const areaLabel = (a: string) => AREA_INFO.find((x) => x.key === a)?.label ?? a;

/** Area picker as switch tiles (same look as the plugin switches in the Admin-Panel). */
export function AreaSwitches({
  value,
  onChange,
  available,
  disabled,
}: {
  value: BackupArea[];
  onChange: (v: BackupArea[]) => void;
  /** Only these can be picked (restore: what the backup contains). */
  available?: BackupArea[];
  disabled?: boolean;
}) {
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {AREA_INFO.filter((a) => !available || available.includes(a.key)).map((a) => {
        const on = value.includes(a.key);
        return (
          <div key={a.key} className="flex items-center justify-between gap-3 rounded-md border px-3 py-2">
            <div className="min-w-0">
              <div className={on ? 'text-sm' : 'text-sm text-muted-foreground'}>{a.label}</div>
              <div className="text-[11px] text-muted-foreground">{a.hint}</div>
            </div>
            <Switch
              checked={on}
              disabled={disabled}
              onChange={(e) => onChange(e.target.checked ? [...value, a.key] : value.filter((x) => x !== a.key))}
              aria-label={`${a.label} ${on ? 'abwählen' : 'auswählen'}`}
            />
          </div>
        );
      })}
    </div>
  );
}

/**
 * Time left from the recent pace (last ~15 s). Deleting is fast, creating
 * slow — so once the current phase has run a few seconds, only its pace counts.
 */
function useTimeLeft(run: BotRun): string | null {
  type Sample = { t: number; done: number; phase: string | null };
  const ref = useRef<{ id: string; samples: Sample[] }>({ id: run.id, samples: [] });
  if (ref.current.id !== run.id) ref.current = { id: run.id, samples: [] };
  const samples = ref.current.samples;
  if (run.status !== 'running' || run.total === 0) return null;
  const now = Date.now();
  const latest = samples[samples.length - 1];
  if (latest?.done !== run.done || latest?.phase !== run.phase) samples.push({ t: now, done: run.done, phase: run.phase });
  while (samples.length > 2 && now - samples[0].t > 15_000) samples.shift();

  const enough = (s: Sample[]) => s.length > 1 && s[s.length - 1].t - s[0].t >= 3000 && s[s.length - 1].done - s[0].done >= 2;
  const inPhase = samples.filter((s) => s.phase === run.phase);
  const basis = enough(inPhase) ? inPhase : samples;
  if (!enough(basis)) return null;
  const first = basis[0];
  const last = basis[basis.length - 1];
  const msLeft = ((run.total - run.done) * (last.t - first.t)) / (last.done - first.done);
  if (msLeft < 60_000) return `ca. ${Math.max(10, Math.round(msLeft / 10_000) * 10)} Sek.`;
  return `ca. ${Math.round(msLeft / 60_000)} Min.`;
}

function ProgressBar({ percent }: { percent: number | null }) {
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-foreground/10">
      {percent === null ? (
        <div className="progress-indeterminate h-full w-1/4 rounded-full bg-primary/70" />
      ) : (
        <div className="h-full rounded-full bg-primary transition-[width] duration-500" style={{ width: `${percent}%` }} />
      )}
    </div>
  );
}

/**
 * One run: while running a bar with percent, "Erstelle Kanäle · 12 / 39",
 * who started it and the time left; afterwards "Fertig ✓" + report (or the
 * error) with an × to hide it.
 */
export function RunProgress({ run }: { run: BotRun }) {
  const me = useMe();
  const timeLeft = useTimeLeft(run);
  const percent = runPercent(run);
  const startedBy = run.requestedBy === me.data?.user.discordId ? 'dir' : run.requestedByName;
  const at = new Date(run.startedAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

  if (run.status === 'running') {
    const phase = run.phase
      ? `${run.phase}${run.phaseTotal > 1 ? ` · ${run.phaseDone} / ${run.phaseTotal}` : ''}`
      : 'Gestartet — warte auf den Bot…';
    return (
      <div className="space-y-2 rounded-md border p-3 text-sm" aria-live="polite">
        <div className="flex items-center justify-between gap-3">
          <span className="flex min-w-0 items-center gap-2">
            <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" />
            <span className="truncate">{phase}</span>
          </span>
          {percent !== null && <span className="shrink-0 font-medium tabular-nums">{percent} %</span>}
        </div>
        <ProgressBar percent={percent} />
        <div className="flex flex-wrap justify-between gap-x-3 text-xs text-muted-foreground">
          <span>
            Gestartet von {startedBy} um {at}
          </span>
          {timeLeft && <span>noch {timeLeft}</span>}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1 rounded-md border p-3 text-sm">
      <div className="flex items-start justify-between gap-2">
        {run.status === 'ok' ? (
          <span className="flex items-center gap-2 font-medium text-success">
            <CheckCircle2 className="h-4 w-4" /> Fertig ✓
          </span>
        ) : (
          <span className="flex items-center gap-2 text-destructive">
            <XCircle className="h-4 w-4 shrink-0" /> Fehlgeschlagen: {run.error ?? 'unbekannter Fehler'}
          </span>
        )}
        <button
          type="button"
          onClick={() => dismissRun(run.id)}
          className="rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
          aria-label="Ausblenden"
          title="Ausblenden"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="text-xs text-muted-foreground">
        Gestartet von {startedBy} um {at}
        {run.finishedAt &&
          ` · fertig um ${new Date(run.finishedAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`}
      </div>
      {run.status === 'ok' && run.action === 'create-backup' && (
        <div className="text-xs text-muted-foreground">Das Backup steht jetzt in der Liste.</div>
      )}
      {!!run.report && <ReportView report={run.report as BackupReport} />}
    </div>
  );
}

export function ReportView({ report }: { report: BackupReport }) {
  const parts = reportParts(report);
  return (
    <div className="space-y-1 text-xs text-muted-foreground">
      {parts.length > 0 && <div>{parts.join(' · ')}</div>}
      {report.areas?.includes('settings') && <div>Bot-Einstellungen übernommen.</div>}
      {!!report.unmapped && (
        <div className="text-amber-600 dark:text-amber-400">
          {report.unmapped} Verweise auf Rollen/Channels konnten nicht zugeordnet werden (gibt es hier nicht mit gleichem Namen).
        </div>
      )}
      {!!report.emojisSkipped?.length && (
        <div className="text-amber-600 dark:text-amber-400">
          Emojis übersprungen (Limit erreicht?): {report.emojisSkipped.join(', ')}
        </div>
      )}
      {report.safetyBackupId && <div>Sicherheits-Backup des Ziels: {report.safetyBackupId}</div>}
      {!!report.warnings?.length && (
        <details>
          <summary className="cursor-pointer">{report.warnings.length} Hinweise</summary>
          <ul className="mt-1 list-disc pl-4">
            {report.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
